import { useEffect, useState } from "react";
import { Button } from "../components/ui/Button";
import { getPrivacy, updatePrivacy, VISIBILITY_LEVELS, type PrivacySettings, type Visibility } from "../lib/privacy";
import { PageHeader } from "../components/Surface";

const inputClass = "rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm text-ink";
const labelClass = "grid gap-1 text-xs font-bold text-muted";

export function Privacy() {
  const [settings, setSettings] = useState<PrivacySettings | null>(null);
  const [realName, setRealName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getPrivacy().then((r) => {
      setSettings(r.settings);
      setRealName(r.contact.real_name ?? "");
      setPhone(r.contact.phone ?? "");
      setEmail(r.contact.email ?? "");
    });
  }, []);

  function setVis(field: keyof PrivacySettings, v: Visibility) {
    setSettings((s) => (s ? { ...s, [field]: v } : s));
  }

  async function save() {
    if (!settings) return;
    setBusy(true);
    setNote(null);
    try {
      await updatePrivacy({ ...settings, real_name: realName, phone });
      setNote("Saved.");
    } catch {
      setNote("Could not save.");
    } finally {
      setBusy(false);
    }
  }

  if (!settings) return <p className="p-10 font-mono text-sm text-muted">Loading…</p>;

  const visSelect = (field: keyof PrivacySettings) => (
    <select className={inputClass} value={settings[field]} onChange={(e) => setVis(field, e.target.value as Visibility)}>
      {VISIBILITY_LEVELS.map((l) => (
        <option key={l} value={l}>
          {l}
        </option>
      ))}
    </select>
  );

  return (
    <div className="mx-auto grid w-full max-w-lg gap-5">
      <PageHeader title="Privacy" subtitle="Your display name is always shown. Choose who can see your real name, email, and phone." />

      <div className="grid gap-4 rounded-card border border-line bg-surface p-4">
        <label className={labelClass}>
          Real name
          <input className={inputClass} value={realName} onChange={(e) => setRealName(e.target.value)} />
        </label>
        <label className={labelClass}>Name visible to{visSelect("name_visibility")}</label>

        <div className="grid gap-1">
          <span className="text-xs font-bold text-muted">Email (your login)</span>
          <input className={`${inputClass} opacity-60`} value={email} readOnly />
        </div>
        <label className={labelClass}>Email visible to{visSelect("email_visibility")}</label>

        <label className={labelClass}>
          Phone
          <input className={inputClass} value={phone} onChange={(e) => setPhone(e.target.value)} />
        </label>
        <label className={labelClass}>Phone visible to{visSelect("phone_visibility")}</label>

        <div className="flex items-center gap-3">
          <Button onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
          {note && <span className="font-mono text-sm text-muted">{note}</span>}
        </div>
      </div>
    </div>
  );
}
